import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  AlertDialog, AlertDialogTrigger, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
  AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from "@/components/ui/alert-dialog";
import { Send, Mic, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";

const KIND_META = {
  note:      { label: "Note",      accent: "#9CA3AF" },
  highlight: { label: "Highlight", accent: "#F4B400" },
  key_point: { label: "Key Point", accent: "#E32424" },
};

/**
 * Live match commentary feed.
 * - When `canPost` is true (umpire/league_admin/super_admin) shows a composer at the top.
 * - Otherwise renders read-only feed for spectators.
 */
export default function MatchCommentary({ matchId, canPost = false }) {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ message: "", kind: "note" });
  const [posting, setPosting] = useState(false);

  useEffect(() => {
    if (!matchId) return;
    let active = true;
    (async () => {
      try {
        const { data } = await api.get(`/matches/${matchId}/commentary`);
        if (active) setItems(data);
      } catch (e) { toast.error(formatApiError(e)); }
      if (active) setLoading(false);
    })();

    // Live updates: posted entries append to local state instantly (see post() below).
    // Cross-client sync intentionally not wired here — keep the page lightweight.

    return () => { active = false; };
  }, [matchId]);

  const post = async (e) => {
    e?.preventDefault();
    if (!form.message.trim()) return;
    setPosting(true);
    try {
      const { data } = await api.post(`/matches/${matchId}/commentary`, form);
      setItems((prev) => [...prev, data]);
      setForm({ message: "", kind: form.kind });
    } catch (e) { toast.error(formatApiError(e)); }
    setPosting(false);
  };

  const del = async (eid) => {
    try {
      await api.delete(`/matches/${matchId}/commentary/${eid}`);
      setItems((prev) => prev.filter((i) => i.id !== eid));
      toast.success("Deleted");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const canDelete = user && (user.role === "super_admin" || user.role === "league_admin");

  return (
    <Card className="p-4 bg-[#11241b] border-white/10" data-testid="match-commentary">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Mic className="w-4 h-4 text-[#00A86B]" />
          <div className="font-anton text-lg uppercase tracking-tight text-white">Live Commentary</div>
        </div>
        <div className="text-[10px] uppercase tracking-widest text-zinc-500">{items.length} entries</div>
      </div>

      {canPost && (
        <form onSubmit={post} className="mb-3 flex flex-col sm:flex-row gap-2" data-testid="commentary-composer">
          <Select value={form.kind} onValueChange={(v) => setForm({ ...form, kind: v })}>
            <SelectTrigger className="w-32 bg-[#060d09] border-white/10 text-white text-xs" data-testid="commentary-kind"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-[#11241b] border-white/10 text-white">
              {Object.entries(KIND_META).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <Input
            data-testid="commentary-input"
            value={form.message}
            onChange={(e) => setForm({ ...form, message: e.target.value })}
            placeholder="e.g. Match point — Lobsters strike at the net!"
            maxLength={500}
            className="flex-1 bg-[#060d09] border-white/10 text-white"
          />
          <Button type="submit" data-testid="commentary-send" disabled={posting || !form.message.trim()} className="bg-[#00A86B] hover:bg-[#00935A] text-white font-bold uppercase tracking-widest">
            <Send className="w-4 h-4 mr-1.5" /> Post
          </Button>
        </form>
      )}

      <div className="space-y-2 max-h-[400px] overflow-y-auto" data-testid="commentary-feed">
        {loading && <div className="text-xs text-zinc-500 py-6 text-center">Loading…</div>}
        {!loading && items.length === 0 && <div className="text-xs text-zinc-500 py-6 text-center" data-testid="commentary-empty">No commentary yet. {canPost && "Be the first."}</div>}
        {items.map((it) => {
          const meta = KIND_META[it.kind] || KIND_META.note;
          return (
            <div key={it.id} className="flex gap-3 items-start border-l-2 pl-3 py-2" style={{ borderColor: meta.accent }} data-testid={`commentary-entry-${it.id}`}>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-zinc-500">
                  <span className="font-bold" style={{ color: meta.accent }}>{meta.label}</span>
                  <span>·</span>
                  <span>{it.author_name}</span>
                  <span>·</span>
                  <span className="font-mono">{it.score_a}–{it.score_b}</span>
                  <span className="text-zinc-600">{new Date(it.created_at).toLocaleTimeString()}</span>
                </div>
                <div className="text-sm text-white mt-0.5">{it.message}</div>
              </div>
              {canDelete && (
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button size="sm" variant="ghost" className="text-red-400 hover:bg-red-500/10 h-7 px-1" data-testid={`commentary-delete-${it.id}`}>
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent className="bg-[#11241b] border-white/10 text-white" data-testid={`commentary-delete-dialog-${it.id}`}>
                    <AlertDialogHeader>
                      <AlertDialogTitle className="font-anton text-2xl uppercase tracking-tight">Delete commentary?</AlertDialogTitle>
                      <AlertDialogDescription className="text-zinc-400">&quot;{it.message}&quot;<br/><span className="text-zinc-600 text-xs">This action cannot be undone.</span></AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel data-testid={`commentary-delete-cancel-${it.id}`} className="border-white/10 text-zinc-300 hover:bg-white/5">Cancel</AlertDialogCancel>
                      <AlertDialogAction data-testid={`commentary-delete-confirm-${it.id}`} onClick={() => del(it.id)} className="bg-red-500 hover:bg-red-600 text-white">
                        Delete
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              )}
            </div>
          );
        })}
      </div>
    </Card>
  );
}
